import { Box, Stack, Typography } from '@mui/material';
import { VIZ } from '../viz/tokens';

interface BarRowProps {
  label: string;
  value: number;
  /** Largest value in the set — sets the scale for every row. */
  max: number;
}

/**
 * One horizontal magnitude bar: label, track, value at the tip.
 *
 * Thin mark, 4px rounded data-end and square at the baseline (the left edge),
 * no border — separation comes from the surface gap in the parent stack, not
 * from a stroke around the fill. The value is text-token ink beside the bar,
 * never coloured like the data.
 */
export default function BarRow({ label, value, max }: BarRowProps) {
  const fraction = max > 0 ? Math.max(value / max, 0) : 0;

  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
      <Typography
        variant="body2"
        sx={{ width: 176, flexShrink: 0, color: 'text.secondary' }}
        noWrap
        title={label}
      >
        {label}
      </Typography>

      <Box sx={{ flexGrow: 1, minWidth: 40 }}>
        <Box
          sx={{
            height: VIZ.barThickness,
            width: `${Math.max(fraction * 100, value > 0 ? 1.5 : 0)}%`,
            bgcolor: VIZ.series,
            borderRadius: `0 ${VIZ.radius}px ${VIZ.radius}px 0`,
          }}
        />
      </Box>

      <Typography
        variant="body2"
        sx={{ width: 56, flexShrink: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
      >
        {value.toLocaleString('en-US')}
      </Typography>
    </Stack>
  );
}